import { isMemberAuthError, retryMemberRequest } from '~/utils/member-auth'

export interface MemberTokenPair {
  access_token: string
  refresh_token: string
  token_type: string
  access_expires_at: string
  refresh_expires_at: string
  login_info?: { client_ip?: string; country?: string; region?: string; city?: string; login_at?: string }
}

const memberTokenStorageKey = 'goblog.member.tokens'
let refreshPromise: Promise<MemberTokenPair | null> | null = null

// Member tokens are scoped to the browser session. They never share keys with
// the administrator console, preventing one UI from overwriting the other.
export function useMemberAuth() {
  const tokens = useState<MemberTokenPair | null>('member-auth-tokens', () => null)

  function restore() {
    if (!import.meta.client || tokens.value) return
    try {
      const raw = window.sessionStorage.getItem(memberTokenStorageKey)
      if (raw) tokens.value = JSON.parse(raw) as MemberTokenPair
    } catch {
      window.sessionStorage.removeItem(memberTokenStorageKey)
    }
  }

  function save(pair: MemberTokenPair) {
    tokens.value = pair
    if (import.meta.client) window.sessionStorage.setItem(memberTokenStorageKey, JSON.stringify(pair))
  }

  function clear() {
    tokens.value = null
    if (import.meta.client) window.sessionStorage.removeItem(memberTokenStorageKey)
  }

  function authorizationHeader() {
    return tokens.value?.access_token ? `Bearer ${tokens.value.access_token}` : ''
  }

  async function refresh() {
    restore()
    const refreshToken = tokens.value?.refresh_token
    if (!refreshToken || !import.meta.client) return null
    if (refreshPromise) return refreshPromise
    const config = useRuntimeConfig()
    const apiBase = String(config.public.apiBaseUrl || '/api/v1').replace(/\/$/, '')
    refreshPromise = (async () => {
      try {
        const response: any = await $fetch(`${apiBase}/custom_user/auth/refresh`, { method: 'POST', body: { refresh_token: refreshToken }, timeout: 10000 })
        const pair = response?.data as MemberTokenPair
        if (!pair?.access_token || !pair?.refresh_token) throw new Error('refresh failed')
        save(pair)
        return pair
      } catch { return null }
      finally { refreshPromise = null }
    })()
    return refreshPromise
  }

  async function memberFetch<T = any>(url: string, options: any = {}): Promise<T> {
    restore()
    const requestOptions = { ...options, headers: { ...(options.headers || {}) } }
    const current = authorizationHeader()
    return retryMemberRequest<T>(async (authorization) => {
      if (authorization) requestOptions.headers.Authorization = authorization
      else delete requestOptions.headers.Authorization
      return $fetch<T>(url, requestOptions)
    }, current, refresh, redirectToLogin)
  }

  function isAuthError(error: any) {
    return isMemberAuthError(error)
  }

  async function redirectToLogin() {
    clear()
    const route = useRoute()
    const redirect = route.fullPath.startsWith('/login') ? '/account' : route.fullPath
    await navigateTo({ path: '/login', query: { redirect } })
  }

  return { tokens, restore, save, clear, authorizationHeader, refresh, memberFetch, isAuthError, redirectToLogin }
}
